import { Subject } from 'rxjs';
import { DashboardService, DashletSettingsService } from '@services/index';
import { Customer, Dashlet, IpnrMap } from '@models/index';

interface Point {
    x: Date;
    y: number;
}

interface MosData {
    unknown: number;
    low: number;
    med: number;
    high: number;
}

export class DashletVoiceQualityActiveStreams extends Dashlet {
    public readonly commandTypeIdRtcpNew = '3A176A87-F14C-447E-AD1F-CD72E35EBC28';
    public readonly commandTypeIdIpnrLookup = '142e7a84-1b04-4c59-a645-9b9b1e5b5312';
    public customer!: Customer;
    public locationId!: string;
    public equipmentIds!: string[];
    public ipnrMap: IpnrMap = new IpnrMap();

    public readonly interval = 20;

    private mosDataStore: Map<number, Map<string, MosData>> = new Map();
    private backfillStore: Map<number, Map<string, MosData>> = new Map();
    private lastProducedTime = 0;

    mosDataGood: Point[] = [];
    mosGood!: number;
    mosDataSatisfactory: Point[] = [];
    mosSatisfactory!: number;
    mosDataPoor: Point[] = [];
    mosPoor!: number;
    mosDataUnknown: Point[] = [];
    mosUnknown!: number;
    groupName!: string;

    private dataUpdated: Subject<null> = new Subject();
    get onDataUpdated() {
        return this.dataUpdated;
    }

    public equipmentLoaded: Subject<null> = new Subject();
    private mosDataTimeoutHandler: ReturnType<typeof setTimeout> | null = null;
    private lastMosTimestamp: Date | null = null;
    private isMosDataStale = false;

    constructor(private dashboardService: DashboardService, private settingsService: DashletSettingsService) {
        super();

        this.sizes = [
            {
                id: 0,
                label: 'Small',
                cols: 5,
                rows: 5
            },
            {
                id: 1,
                label: 'Large',
                cols: 10,
                rows: 5
            }
        ];
        this.applySize(0);

        // delay the polling by 1 interval to account for updates lagging behind real-time
        this.dashboardService.getUpdateInterval(this.interval).subscribe(time => {
            this.produceData(new Date(time.getTime() - 20 * 1000));
        });
    }

    applySettings(v: { [key: string]: any }) {
        super.applySettings(v);
        this.configured = v.customer && v.location && v.equipment;

        if (v.customer) {
            this.customer = new Customer(v.customer.value, v.customer.label);
        } else {
            this.customer = new Customer('', '');
        }
        this.locationId = v.location && v.location.value ? v.location.value : null;
        const equipValue: string = v.equipment && v.equipment.value ? v.equipment.value : null;
        this.generatedNameTag =
            v.location && v.equipment ? `${v.location.label} | ${v.equipment.label}` : 'Unconfigured';
        this.customNameTag = v.nameTag;

        if (equipValue && this.locationId) {
            this.configured = true;

            if (equipValue === '=all=') {
                this.settingsService.getRtcpReceivers(this.locationId).subscribe(res => {
                    if (res) {
                        this.equipmentIds = res.map(receiver => receiver.value);
                        this.equipmentLoaded.next(null);
                    }
                });
            } else {
                this.equipmentIds = [equipValue];
                this.equipmentLoaded.next(null);
            }
        }
    }

    dispose() {}

    resetData() {
        this.mosDataStore.clear();
        this.backfillStore.clear();
        this.mosDataGood = [];
        this.mosDataSatisfactory = [];
        this.mosDataPoor = [];
        this.mosDataUnknown = [];

        this.mosGood = 0;
        this.mosSatisfactory = 0;
        this.mosPoor = 0;
        this.mosUnknown = 0;

        this.dataUpdated.next(null);
    }

    public processRtcpNew(sourceKey: string, data: any) {
        if (!data || data.length === 0) return;
        const timestampStr = data?.[0]?.timestamp;
        this.lastMosTimestamp = timestampStr ? new Date(timestampStr) : new Date();
        const roundedTimestamp = new Date(this.lastMosTimestamp);
        roundedTimestamp.setSeconds(Math.ceil(roundedTimestamp.getSeconds() / 20) * 20, 0);

        const mosData = this.setBlankSource(roundedTimestamp.getTime(), sourceKey);

        mosData.unknown = +(data?.[0]?.data?.[0]?.MosUnknown ?? 0);
        mosData.low = +(data?.[0]?.data?.[0]?.MosLow ?? 0);
        mosData.med = +(data?.[0]?.data?.[0]?.MosMedium ?? 0);
        mosData.high = +(data?.[0]?.data?.[0]?.MosHigh ?? 0);
        this.resetMosDataTimeout();
    }

    public startBackfill(): void {
        this.backfillStore.clear();
    }

    public processRtcpBackfill(sourceKey: string, docs: any[], from: Date): void {
        if (!docs || docs.length === 0) return;

        const earliest = new Date();
        if (this.getSize().id === 0) {
            earliest.setMinutes(earliest.getMinutes() - 30);
        } else if (this.getSize().id === 1) {
            earliest.setMinutes(earliest.getMinutes() - 60);
        }
        const lowerBound = Math.max(from.getTime(), earliest.getTime());

        const sortedDocs = docs
            .filter(doc => doc?.timestamp)
            .sort((a, b) => Date.parse(a.timestamp) - Date.parse(b.timestamp));

        const producedKeys = new Set<number>();
        sortedDocs.forEach(doc => {
            // for each document we get we want to round it up to the nearest polling interval and add it to the Map<rounded time, data>
            const roundedTimestamp = new Date(doc.timestamp);
            roundedTimestamp.setSeconds(Math.ceil(roundedTimestamp.getSeconds() / this.interval) * this.interval, 0);
            const timeKey = roundedTimestamp.getTime();
            if (timeKey <= lowerBound) return;

            const mosData: MosData = {
                unknown: +(doc.data?.[0]?.MosUnknown ?? 0),
                low: +(doc.data?.[0]?.MosLow ?? 0),
                med: +(doc.data?.[0]?.MosMedium ?? 0),
                high: +(doc.data?.[0]?.MosHigh ?? 0)
            };

            if (timeKey > this.lastProducedTime) {
                // this means that the graph hasn't drawn this point so put it straight into mosDataStore (live object)
                if (!this.mosDataStore.get(timeKey)?.has(sourceKey)) {
                    Object.assign(this.setBlankSource(timeKey, sourceKey), mosData);
                }
                return;
            }

            // otherwise, we add it to the backfillStore for redrawing it after the foreach
            let sources = this.backfillStore.get(timeKey);
            if (!sources) {
                sources = new Map<string, MosData>();
                this.backfillStore.set(timeKey, sources);
            }
            sources.set(sourceKey, mosData);
            producedKeys.add(timeKey);
        });

        if (producedKeys.size === 0) return;

        producedKeys.forEach(timeKey => {
            let unknown = 0,
                low = 0,
                med = 0,
                high = 0;
            for (const sourceData of this.backfillStore.get(timeKey)!.values()) {
                unknown += sourceData.unknown;
                low += sourceData.low;
                med += sourceData.med;
                high += sourceData.high;
            }
            const x = new Date(timeKey);
            this.upsertPoint(this.mosDataUnknown, x, unknown);
            this.upsertPoint(this.mosDataPoor, x, low);
            this.upsertPoint(this.mosDataSatisfactory, x, med);
            this.upsertPoint(this.mosDataGood, x, high);
        });

        this.dataUpdated.next(null);
    }

    private upsertPoint(points: Point[], x: Date, y: number): void {
        const existing = points.find(p => p.x.getTime() === x.getTime());
        if (existing) {
            existing.y = y;
            return;
        }
        const insertAt = points.findIndex(p => p.x.getTime() > x.getTime());
        if (insertAt === -1) {
            points.push({ x, y });
        } else {
            points.splice(insertAt, 0, { x, y });
        }
    }

    private resetMosDataTimeout() {
        if (this.mosDataTimeoutHandler) {
            clearTimeout(this.mosDataTimeoutHandler);
        }
        this.mosDataTimeoutHandler = setTimeout(() => {
            if (this.lastMosTimestamp) {
                const now = new Date();
                const diff = now.getTime() - this.lastMosTimestamp.getTime();
                if (diff >= 60000 && document.visibilityState === 'visible') {
                    this.isMosDataStale = true;
                }
            }
        }, 60000);
        this.isMosDataStale = false;
    }

    private setBlankSource(timeKey: number, sourceKey: string): MosData {
        let sources = this.mosDataStore.get(timeKey);

        if (!sources) {
            sources = new Map<string, MosData>();
            this.mosDataStore.set(timeKey, sources);
        }

        if (!sources.has(sourceKey)) {
            sources.set(sourceKey, {
                unknown: 0,
                low: 0,
                med: 0,
                high: 0
            });
        }
        return sources.get(sourceKey)!;
    }

    private produceData(time: Date): void {
        this.lastProducedTime = time.getTime();
        if (!this.isMosDataStale && this.lastMosTimestamp) {
            const now = new Date();
            const diff = now.getTime() - this.lastMosTimestamp.getTime();
            if (diff >= 120000) {
                this.isMosDataStale = true;
            }
        }
        if (this.isMosDataStale) {
            this.mosGood = 0;
            this.mosSatisfactory = 0;
            this.mosPoor = 0;
            this.mosUnknown = 0;
            this.mosDataPoor.push({
                x: time,
                y: 0
            });
            this.mosDataSatisfactory.push({
                x: time,
                y: 0
            });
            this.mosDataGood.push({
                x: time,
                y: 0
            });
            this.mosDataUnknown.push({
                x: time,
                y: 0
            });
            this.dataUpdated.next(null);
            return;
        }
        const earliest = new Date(time);

        if (this.getSize().id === 0) {
            earliest.setMinutes(earliest.getMinutes() - 30);
        } else if (this.getSize().id === 1) {
            earliest.setMinutes(earliest.getMinutes() - 60);
        }

        const thresFunc = (p: Point) => p.x > earliest;
        this.mosDataPoor = this.mosDataPoor.filter(thresFunc);
        this.mosDataSatisfactory = this.mosDataSatisfactory.filter(thresFunc);
        this.mosDataGood = this.mosDataGood.filter(thresFunc);
        this.mosDataUnknown = this.mosDataUnknown.filter(thresFunc);

        const currentMosData = this.mosDataStore.get(time.getTime());

        if (!currentMosData) {
            return;
        }

        let unknown = 0,
            low = 0,
            med = 0,
            high = 0;

        for (const sourceData of currentMosData.values()) {
            unknown += sourceData.unknown;
            low += sourceData.low;
            med += sourceData.med;
            high += sourceData.high;
        }
        this.mosDataStore.delete(time.getTime());

        this.mosDataUnknown.push({
            x: time,
            y: unknown
        });
        this.mosUnknown = unknown;

        this.mosDataPoor.push({
            x: time,
            y: low
        });
        this.mosPoor = low;

        this.mosDataSatisfactory.push({
            x: time,
            y: med
        });
        this.mosSatisfactory = med;

        this.mosDataGood.push({
            x: time,
            y: high
        });
        this.mosGood = high;
        this.dataUpdated.next(null);
    }
}
